// @/core/router/router.ts

import { authStore } from "@/core/store/auth.store";
// --- Definiciones de Tipos para TypeScript ---
interface PageModule {
  init: () => void;
  destroy?: () => void; // La función destroy es opcional
}

interface Route {
  // Fase 5 (bundle): el HTML se descarga bajo demanda igual que el JS,
  // para que el paquete inicial no cargue las 20 pantallas de golpe.
  html: () => Promise<string>;
  js: () => Promise<PageModule>;
  meta?: {
    requiresAuth?: boolean;
    keepAlive?: boolean; // <-- AÑADIDO: Para rutas que no se deben destruir
    hideDashboard?: boolean; // <-- AÑADIDO: Oculta cabeceras/sidebar
  };
}

type Routes = {
  [key: string]: Route;
};

// --- 2. DEFINICIÓN COMPLETA DE RUTAS ---
const routes: Routes = {
  "/ia-ventas": {
    html: () => import("@/pages/sales-ai/sales-ai.html?raw").then((m) => m.default),
    js: () => import("@/pages/sales-ai/sales-ai"),
    meta: { requiresAuth: true },
  },
  "/": {
    html: () => import("@/pages/dashboard/dashboard.html?raw").then((m) => m.default),
    js: () => import("@/pages/dashboard/dashboard"),
    meta: { requiresAuth: true },
  },
  "/auth": {
    html: () => import("@/pages/auth/auth.html?raw").then((m) => m.default),
    js: () => import("@/pages/auth/auth"),
    meta: { requiresAuth: false },
  },
  "/dashboard": {
    html: () => import("@/pages/dashboard/dashboard.html?raw").then((m) => m.default),
    js: () => import("@/pages/dashboard/dashboard"),
    meta: { requiresAuth: true },
  },
  "/ventas": {
    html: () => import("@/pages/ventas/ventas.html?raw").then((m) => m.default),
    js: () => import("@/pages/ventas/ventas"),
    meta: { requiresAuth: true },
  },
  "/inventario": {
    html: () => import("@/pages/inventario/inventario.html?raw").then((m) => m.default),
    js: () => import("@/pages/inventario/inventario"),
    meta: { requiresAuth: true, keepAlive: true }, // <-- AÑADIDO: Evita recargar imágenes innecesariamente
  },
  "/egresosnegocio": {
    html: () => import("@/pages/egresosnegocio/egresosnegocio.html?raw").then((m) => m.default),
    js: () => import("@/pages/egresosnegocio/egresosnegocio"),
    meta: { requiresAuth: true, keepAlive: true }, // cachea la vista para no releer Firestore al volver
  },
  "/gastospersonales": {
    html: () => import("@/pages/gastospersonales/gastospersonales.html?raw").then((m) => m.default),
    js: () => import("@/pages/gastospersonales/gastospersonales"),
    meta: { requiresAuth: true, keepAlive: true }, // cachea la vista para no releer Firestore al volver
  },
  "/otrosingresos": {
    html: () => import("@/pages/otrosingresos/otrosingresos.html?raw").then((m) => m.default),
    js: () => import("@/pages/otrosingresos/otrosingresos"),
    meta: { requiresAuth: true, keepAlive: true }, // cachea la vista para no releer Firestore al volver
  },
  "/clientes": {
    html: () => import("@/pages/clientes/clientes.html?raw").then((m) => m.default),
    js: () => import("@/pages/clientes/clientes"),
    meta: { requiresAuth: true, keepAlive: true }, // <-- AÑADIDO
  },
  "/crm-whatsapp": {
    html: () => import("@/pages/crm-whatsapp/menu-whatsapp.html?raw").then((m) => m.default),
    js: () => import("@/pages/crm-whatsapp/menu-whatsapp"),
    meta: { requiresAuth: true },
  },
  "/crm-whatsapp/chat": {
    html: () => import("@/pages/crm-whatsapp/crm-whatsapp.html?raw").then((m) => m.default),
    js: () => import("@/pages/crm-whatsapp/crm-whatsapp"),
    meta: { requiresAuth: true },
  },
  "/crm-whatsapp/dashboard": {
    html: () => import("@/pages/crm-whatsapp/dashboard-whatsapp.html?raw").then((m) => m.default),
    js: () => import("@/pages/crm-whatsapp/dashboard-whatsapp"),
    meta: { requiresAuth: true },
  },
  "/crm-whatsapp/retargeting": {
    html: () => import("@/pages/crm-whatsapp/quetzalito-retargeting.html?raw").then((m) => m.default),
    js: () => import("@/pages/crm-whatsapp/quetzalito-retargeting"),
    meta: { requiresAuth: true },
  },
  "/tiendaweb": {
    html: () => import("@/pages/tiendaweb/tiendaweb.html?raw").then((m) => m.default),
    js: () => import("@/pages/tiendaweb/tiendaweb"),
    meta: { requiresAuth: true },
  },
  "/tiendaweb/editor": {
    html: () => import("@/pages/tiendaweb/editor/editor.html?raw").then((m) => m.default),
    js: () => import("@/pages/tiendaweb/editor/editor"),
    meta: { requiresAuth: true, hideDashboard: true }, // constructor a pantalla completa
  },
  "/vistacliente": {
    html: () => import("@/pages/vistacliente/vistacliente.html?raw").then((m) => m.default),
    js: () => import("@/pages/vistacliente/vistacliente"),
    meta: { requiresAuth: true },
  },
  "/configuracion": {
    html: () => import("@/pages/configuracion/configuracion.html?raw").then((m) => m.default),
    js: () => import("@/pages/configuracion/configuracion"),
    meta: { requiresAuth: true },
  },
  "/admin-quetzalbot": {
    html: () => import("@/pages/canvas/admin-quetzalbot.html?raw").then((m) => m.default),
    js: () => import("@/pages/canvas/admin-quetzalbot"),
    meta: { requiresAuth: true },
  },
  "/quetzalito": {
    html: () => import("@/pages/quetzalito/quetzalito.html?raw").then((m) => m.default),
    js: () => import("@/pages/quetzalito/quetzalito"),
    meta: { requiresAuth: true },
  },
  "/uso-facturacion": {
    html: () => import("@/pages/uso-facturacion/uso-facturacion.html?raw").then((m) => m.default),
    js: () => import("@/pages/uso-facturacion/uso-facturacion"),
    meta: { requiresAuth: true },
  },
  "/perfil": {
    html: () => import("@/pages/perfil/perfil.html?raw").then((m) => m.default),
    js: () => import("@/pages/perfil/perfil"),
    meta: { requiresAuth: true },
  },
  "/admin/billing": {
    html: () => import("@/pages/admin-billing/admin-billing.html?raw").then((m) => m.default),
    js: () => import("@/pages/admin-billing/admin-billing"),
    meta: { requiresAuth: true, hideDashboard: true },
  },
  "/planes": {
    html: () => import("@/pages/planes/planes.html?raw").then((m) => m.default),
    js: () => import("@/pages/planes/planes"),
    meta: { requiresAuth: true, hideDashboard: true },
  },
  "/replay": {
    html: () => import("@/pages/replay/replay.html?raw").then((m) => m.default),
    js: () => import("@/pages/replay/replay"),
    meta: { requiresAuth: true, hideDashboard: true },
  },
};

const appOutlet = document.getElementById("app-router-outlet");
let currentPageModule: PageModule | null = null;
let currentPagePath: string | null = null;

// Caché para las páginas con "keepAlive"
const pageCache: {
  [path: string]: { module: PageModule; element: HTMLElement };
} = {};

/**
 * Oculta o muestra los elementos del dashboard (sidebar y header profile) según la ruta.
 * @param isAuthPage Si es true, oculta los elementos. Si es false, los muestra.
 */
function toggleDashboardElements(hideDashboard: boolean): void {
  const sidebar = document.getElementById("appSidebar");
  const sidebarContainer = document.getElementById("sidebar-container");
  const headerProfile = document.getElementById("app-header-profile-container");
  const navbarContainer = document.getElementById("navbar-container");
  const mainContentWrapper = document.querySelector(".main-content-wrapper");

  const path = window.location.pathname;
  const isAuthPage = path === "/auth";
  const isLoggedIn = authStore.getState().isLoggedIn;

  // Mostrar el perfil flotante si el usuario está autenticado y no está en la página de login
  if (headerProfile) {
    if (isLoggedIn && !isAuthPage) {
      headerProfile.style.display = "block";
      document.body.classList.add("show-profile-header");
    } else {
      headerProfile.style.display = "none";
      document.body.classList.remove("show-profile-header");
    }
  }

  if (hideDashboard) {
    // Ocultar elementos del dashboard
    if (sidebar) sidebar.style.display = "none";
    if (sidebarContainer) sidebarContainer.style.display = "none";
    if (navbarContainer) navbarContainer.style.display = "none";
    if (mainContentWrapper) {
      (mainContentWrapper as HTMLElement).style.marginLeft = "0";
    }
    // Cambiar clase del body
    document.body.classList.remove("page-app");
    document.body.classList.add("page-auth");
    document.body.classList.remove("is-crm-page");
  } else {
    // Mostrar elementos del dashboard
    if (sidebar) sidebar.style.display = "";
    if (sidebarContainer) sidebarContainer.style.display = "";
    if (navbarContainer) navbarContainer.style.display = "";
    if (mainContentWrapper) {
      (mainContentWrapper as HTMLElement).style.marginLeft = "";
    }
    // Cambiar clase del body
    document.body.classList.remove("page-auth");
    document.body.classList.add("page-app");

    // Detectar si estamos en el CRM Chat para ajustar colores del sidebar
    if (path === "/crm-whatsapp/chat") {
      document.body.classList.add("is-crm-page");
    } else {
      document.body.classList.remove("is-crm-page");
    }
  }
}

/**
 * Normaliza una ruta para asegurar consistencia.
 * - Elimina query strings y hashes.
 * - Elimina la extensión .html.
 * - Elimina '/index'.
 * - Se asegura de que no haya una barra al final (a menos que sea la raíz '/').
 * @param path La ruta a normalizar.
 */
function normalizePath(path: string): string {
  let cleanPath = path.split("?")[0].split("#")[0];

  if (cleanPath.endsWith(".html")) {
    cleanPath = cleanPath.slice(0, -5);
  }
  if (cleanPath.endsWith("/index")) {
    cleanPath = cleanPath.slice(0, -6);
  }
  if (cleanPath.length > 1 && cleanPath.endsWith("/")) {
    cleanPath = cleanPath.slice(0, -1);
  }
  if (cleanPath === "") {
    return "/";
  }
  return cleanPath;
}

/**
 * Carga la vista y el script de la ruta actual, y destruye el módulo anterior.
 */
const router = async (): Promise<void> => {
  if (!appOutlet) {
    console.error('El elemento "app-router-outlet" no se encontró en el DOM.');
    return;
  }

  const targetPath = normalizePath(window.location.pathname);
  const targetRoute = routes[targetPath];

  // --- LÓGICA DE DESTRUCCIÓN O CACHÉ DE LA PÁGINA ANTERIOR ---
  if (currentPageModule && currentPagePath) {
    const currentRoute = routes[currentPagePath];
    if (currentRoute?.meta?.keepAlive) {
      // Guardar en caché en lugar de destruir
      console.log(`[Router] Guardando en caché la página: ${currentPagePath}`);
      const cacheContainer = document.createElement("div");
      while (appOutlet.firstChild) {
        cacheContainer.appendChild(appOutlet.firstChild);
      }
      pageCache[currentPagePath] = {
        module: currentPageModule,
        element: cacheContainer,
      };
    } else if (typeof currentPageModule.destroy === "function") {
      // Destruir normalmente
      try {
        currentPageModule.destroy();
        console.log(
          `[Router] Módulo de página anterior (${currentPagePath}) destruido.`,
        );
      } catch (error) {
        console.error(
          `[Router] Error al destruir el módulo (${currentPagePath}):`,
          error,
        );
      }
    }
  }
  currentPageModule = null;
  appOutlet.innerHTML = "";

  // --- LÓGICA DE AUTENTICACIÓN Y REDIRECCIÓN ---
  if (!targetRoute) {
    const userRol = authStore.getState().user?.rol;
    const fallbackPath = authStore.getState().isLoggedIn
      ? (userRol === 'admin' ? '/admin/billing' : '/dashboard')
      : '/auth';
    console.warn(
      `Ruta no encontrada: '${targetPath}'. Redirigiendo a '${fallbackPath}'.`,
    );
    if (targetPath !== fallbackPath) {
      history.replaceState(null, "", fallbackPath);
      await router();
    }
    return;
  }

  const { isLoggedIn, user } = authStore.getState();

  if (targetRoute.meta?.requiresAuth && !isLoggedIn) {
    if (targetPath !== "/auth") {
      history.pushState(null, "", "/auth");
      await router();
    }
    return;
  }

  const esAdmin = user?.rol === 'admin';

  if (targetPath === "/auth" && isLoggedIn) {
    const redirectPath = esAdmin ? '/admin/billing' : '/dashboard';
    history.pushState(null, "", redirectPath);
    await router();
    return;
  }

  // --- OCULTAR/MOSTRAR ELEMENTOS DEL DASHBOARD SEGÚN LA RUTA ---
  const hideDashboard = targetPath === "/auth" || !!targetRoute.meta?.hideDashboard || user?.rol === 'admin';
  toggleDashboardElements(hideDashboard);

  // --- LÓGICA DE CARGA O RESTAURACIÓN DE LA PÁGINA NUEVA ---
  try {
    if (targetRoute.meta?.keepAlive && pageCache[targetPath]) {
      // Restaurar desde caché
      console.log(`[Router] Restaurando desde caché la página: ${targetPath}`);
      const { module, element } = pageCache[targetPath];
      while (element.firstChild) {
        appOutlet.appendChild(element.firstChild);
      }
      currentPageModule = module;
    } else {
      // Carga normal: HTML y JS se descargan en paralelo (Fase 5, bundle)
      const [pageHtml, pageModule] = await Promise.all([
        targetRoute.html(),
        targetRoute.js(),
      ]);
      appOutlet.innerHTML = pageHtml;

      if (pageModule && typeof pageModule.init === "function") {
        pageModule.init();
        currentPageModule = pageModule;
      } else {
        console.error(
          `El módulo para la ruta '${targetPath}' no tiene una función 'init' exportada.`,
        );
      }
    }
    currentPagePath = targetPath;

    // --- Emitir evento de cambio de ruta para que componentes como el sidebar se actualicen ---
    window.dispatchEvent(
      new CustomEvent("route-changed", { detail: { path: targetPath } }),
    );
  } catch (error) {
    console.error(`Error al procesar la ruta '${targetPath}':`, error);
    appOutlet.innerHTML = `<div class="p-8 text-center text-red-500"><h1>Error al cargar la página.</h1></div>`;
  }
};

/**
 * Inicializa el enrutador y los listeners para la navegación SPA.
 */
export const initializeRouter = (): void => {
  window.addEventListener("click", (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const link = target.closest("a[data-link]");
    if (link) {
      e.preventDefault();
      const href = link.getAttribute("href");
      if (!href) return;
      // Compara pathname+querystring completos, no solo el pathname normalizado:
      // dos enlaces a la MISMA ruta con distinto querystring (p. ej. el editor de
      // Tienda Web, que identifica la página vía ?tienda=&pagina=) antes se
      // consideraban "el mismo destino" y el clic no navegaba a ningún lado.
      const destino = new URL(href, window.location.origin);
      const actual = window.location.pathname + window.location.search;
      if (destino.pathname + destino.search !== actual) {
        history.pushState(null, "", href);
        router();
      }
    }
  });

  window.addEventListener("popstate", router);

  // Escuchar cambios en el estado de autenticación para redirigir cuando sea necesario
  window.addEventListener("auth-state-ready", () => {
    router();
  });

  router();
};
